import { z } from 'zod';
import { IsoDateTimeSchema } from './common.js';
import { DeviceCapabilitySchema, DeviceIdentityFieldsSchema, DevicePlatformSchema } from './device-identity.js';
import { ScreenRotationDegreesSchema } from './screen-rotation.js';
import { IdleDisplaySchema } from './organisation-settings.js';
import { DeviceWallAssignmentSchema } from './video-wall.js';
export const DeviceSyncRequestSchema = z
    .object({
    deviceId: z.string().min(1),
    playlistVersion: z.number().int().nonnegative(),
    appVersion: z.string().min(1),
    freeStorageBytes: z.number().int().nonnegative().nullish(),
    /** Android UI / Activity render size — may differ from HDMI output on Fire TV. */
    renderWidthPx: z.number().int().positive().nullish(),
    renderHeightPx: z.number().int().positive().nullish(),
    densityDpi: z.number().int().positive().nullish(),
    orientation: z.enum(['landscape', 'portrait']).nullish(),
    /** Physical / HDMI active mode when observable; omit rather than inventing from render. */
    outputWidthPx: z.number().int().positive().nullish(),
    outputHeightPx: z.number().int().positive().nullish(),
    refreshRateHz: z.number().positive().nullish(),
})
    .merge(DeviceIdentityFieldsSchema);
/**
 * Full wire set delivered on sync. Includes reserved types that operators
 * cannot enqueue (see OperatorDeviceCommandTypeSchema) so old pending rows
 * still parse; players must fail those honestly rather than silent-success.
 */
export const DeviceCommandTypeSchema = z.enum([
    'REFRESH_CONTENT',
    'RESTART_PLAYER',
    'CLEAR_CACHE',
    'REQUEST_SCREENSHOT',
    'SYNC_NOW',
    'COLLECT_LOGS',
]);
export const DeviceCommandSchema = z.object({
    id: z.string().min(1),
    type: DeviceCommandTypeSchema,
    payload: z.record(z.unknown()).optional(),
});
/** One daypart/promo window pinned to a published playlist version (ADR 0005). */
export const DeviceScheduleWindowSchema = z.object({
    id: z.string().min(1),
    playlistId: z.string().min(1),
    playlistVersion: z.number().int().positive(),
    startLocalTime: z.string().regex(/^\d{2}:\d{2}$/),
    endLocalTime: z.string().regex(/^\d{2}:\d{2}$/),
    daysOfWeek: z.array(z.number().int().min(0).max(6)),
    priority: z.number().int(),
    targetSpecificity: z.enum(['screen', 'group']),
    updatedAt: IsoDateTimeSchema,
});
export const DeviceScheduleSnapshotSchema = z.object({
    timeZone: z.string().min(1),
    defaultPlaylistId: z.string().min(1),
    defaultPlaylistVersion: z.number().int().positive(),
    windows: z.array(DeviceScheduleWindowSchema),
});
export const DeviceSyncResponseSchema = z.object({
    desiredPlaylistVersion: z.number().int().nonnegative(),
    /** Playlist that owns [desiredPlaylistVersion]; null when nothing is assigned. */
    desiredPlaylistId: z.string().min(1).nullable().default(null),
    /** Present when the screen has a default assignment; enables offline dayparting. */
    schedule: DeviceScheduleSnapshotSchema.nullable().default(null),
    commands: z.array(DeviceCommandSchema).default([]),
    serverTime: IsoDateTimeSchema,
    /**
     * When the server received this request and when it sent the response, for the
     * four-timestamp clock calculation (video walls). Optional for older API builds.
     */
    serverReceivedAt: IsoDateTimeSchema.optional(),
    serverSentAt: IsoDateTimeSchema.optional(),
    /**
     * How media should paint on this panel. Additive (ADR 0006); defaults to fit
     * so older API builds and fixtures remain valid.
     */
    scaleMode: z.enum(['fit', 'fill']).default('fit'),
    /**
     * Desired presentation rotation in degrees. Additive (ADR 0006); defaults to
     * 0 so older API builds and fixtures remain valid. Players rotate the stage;
     * media assets are not rewritten. Fit/Fill apply in the rotated coordinate space.
     */
    rotationDegrees: ScreenRotationDegreesSchema.default(0),
    /**
     * Active video-wall tile for this screen (ADR 0007). Null when the screen is
     * not on a wall or the wall has no active publication. Additive — older
     * players ignore the field.
     */
    wallAssignment: DeviceWallAssignmentSchema.nullable().default(null),
    /**
     * How often the player should poll sync. Additive — older players ignore it
     * and keep their compiled default (30s).
     */
    syncIntervalMs: z.number().int().positive().default(30_000),
    /**
     * What to show when paired but nothing is playing. Additive — older players
     * ignore it.
     */
    idleDisplay: IdleDisplaySchema.default('logo'),
});
export const DeviceCommandAckSchema = z.object({
    commandId: z.string().min(1),
    status: z.enum(['ACKNOWLEDGED', 'EXECUTED', 'FAILED']),
    message: z.string().optional(),
});
/** Dashboard-facing command status (read model for the status-polling endpoint). */
export const DeviceCommandStatusSchema = z.object({
    id: z.string().min(1),
    deviceId: z.string().min(1),
    type: DeviceCommandTypeSchema,
    status: z.enum(['pending', 'sent', 'acknowledged', 'executed', 'failed', 'expired']),
    resultMessage: z.string().nullable(),
    createdAt: IsoDateTimeSchema,
    sentAt: IsoDateTimeSchema.nullable(),
    acknowledgedAt: IsoDateTimeSchema.nullable(),
    /** Null for reserved/non-policy command types, which never expire (see COMMAND_TTL_SECONDS). */
    expiresAt: IsoDateTimeSchema.nullable(),
});
/** Dashboard-facing fleet view: one row per device. */
export const DeviceSummarySchema = z.object({
    id: z.string().min(1),
    organisationId: z.string().min(1),
    screenId: z.string().nullable(),
    deviceCode: z.string().min(1),
    label: z.string().nullable(),
    status: z.enum(['active', 'revoked']),
    appVersion: z.string().nullable(),
    /** Null when the player has never reported identity (ADR 0006). */
    platform: DevicePlatformSchema.nullable(),
    playerBuild: z.string().min(1).nullable(),
    capabilities: z.array(DeviceCapabilitySchema).nullable(),
    manufacturer: z.string().min(1).nullable(),
    model: z.string().min(1).nullable(),
    currentPlaylistVersion: z.number().int().nonnegative().nullable(),
    lastHeartbeatAt: IsoDateTimeSchema.nullable(),
    online: z.boolean(),
    renderWidthPx: z.number().int().positive().nullable(),
    renderHeightPx: z.number().int().positive().nullable(),
    densityDpi: z.number().int().positive().nullable(),
    orientation: z.enum(['landscape', 'portrait']).nullable(),
    outputWidthPx: z.number().int().positive().nullable(),
    outputHeightPx: z.number().int().positive().nullable(),
    refreshRateHz: z.number().positive().nullable(),
});
/** Bounded display history row (change-point samples; not every heartbeat). */
export const DeviceDisplaySampleSchema = z.object({
    id: z.string().min(1),
    deviceId: z.string().min(1),
    recordedAt: IsoDateTimeSchema,
    renderWidthPx: z.number().int().positive().nullable(),
    renderHeightPx: z.number().int().positive().nullable(),
    densityDpi: z.number().int().positive().nullable(),
    orientation: z.enum(['landscape', 'portrait']).nullable(),
    outputWidthPx: z.number().int().positive().nullable(),
    outputHeightPx: z.number().int().positive().nullable(),
    refreshRateHz: z.number().positive().nullable(),
});
/**
 * One row in a device's command history. Payload is intentionally
 * omitted (per task scope -- it may contain sensitive information).
 * `type` covers the full wire enum, including reserved historical types
 * that are no longer operator-enqueueable but may still exist in history.
 */
export const DeviceCommandHistoryItemSchema = z.object({
    id: z.string().min(1),
    type: DeviceCommandTypeSchema,
    status: z.enum(['pending', 'sent', 'acknowledged', 'executed', 'failed', 'expired']),
    /** Null for historical rows predating attribution, or system-issued commands. */
    issuedByUserId: z.string().min(1).nullable(),
    /** Immutable display-name snapshot taken when the command was created -- never a live lookup. */
    issuedByDisplayName: z.string().nullable(),
    createdAt: IsoDateTimeSchema,
    sentAt: IsoDateTimeSchema.nullable(),
    acknowledgedAt: IsoDateTimeSchema.nullable(),
    expiresAt: IsoDateTimeSchema.nullable(),
    resultMessage: z.string().nullable(),
});
/** Opaque, base64url-encoded keyset cursor -- callers must never construct or inspect it. */
export const DeviceCommandHistoryResponseSchema = z.object({
    items: z.array(DeviceCommandHistoryItemSchema),
    nextCursor: z.string().nullable(),
});
/** GET /v1/device/time: lightweight clock sample for video walls (four-timestamp sync). */
export const DeviceTimeResponseSchema = z.object({
    /** Stamped as soon as the request reached the server, before auth or any database work. */
    serverReceivedAt: IsoDateTimeSchema,
    /** Stamped just before the response was sent. */
    serverSentAt: IsoDateTimeSchema,
});
//# sourceMappingURL=device.js.map